/**
 * Guns, missiles and things going bang — M6's weapons, built now.
 *
 * ```
 *   cannon:    baked transient ─┬─► out
 *                               └─► delay(38 ms) ─► lowpass(1.8 k) ─► feedback(0.32) ─► out
 *   missile:   white ─► bandpass(swept 2.6 k → 300 Hz over 1.4 s) ─► envelope ─► out
 *   explosion: baked body ─► lowpass(f ← distance) ─► envelope ─► out
 * ```
 *
 * ## The room, and why it is a delay rather than a reverb
 *
 * CLAUDE.md asks for "cannon (per-shot transient + room)", and the obvious implementation is a
 * `ConvolverNode` with a baked impulse response. It is also the wrong one here: a convolver runs
 * a partitioned FFT on every sample of every buffer that reaches it, for the whole session,
 * whether or not anything is firing. This milestone's budget for the entire audio system is
 * 0.5 ms a frame.
 *
 * A short feedback delay with a lowpass in the loop gives the thing that actually matters — the
 * sense that the report went somewhere and came back — for three nodes and no cost when the bus
 * is silent. It is not a room simulation. It is a room *impression*, which at 300 m and 0.3 s is
 * all anyone can hear anyway.
 *
 * ## Why the missile is built live and everything else is baked
 *
 * A whoosh is a filter sweep over more than a second, and baking one means baking the sweep — so
 * every missile would be identical, and a salvo of four would phase against itself audibly.
 * Built live, each one gets its own sweep and its own randomised timing, and the node churn is
 * four objects on a keypress rather than anything in the frame path.
 *
 * `updateFrame(alpha)`: none. This voice is entirely event-driven; there is nothing continuous
 * to update.
 */

import type { Rng } from '../../core/rng.js';
import type { AudioBank } from '../bank.js';
import { distanceGain, explosionCutoffHz } from '../model.js';
import { percussive, setImmediate } from '../param.js';
import { OneShotPool, disconnectAll, gainNode, lowpass } from './parts.js';

export class OrdnanceVoice {
  private readonly context: AudioContext;
  private readonly bank: AudioBank;
  private readonly rng: Rng;

  private readonly dry: GainNode;
  private readonly roomDelay: DelayNode | null;
  private readonly roomTone: BiquadFilterNode | null;
  private readonly roomFeedback: GainNode | null;
  private readonly roomLevel: GainNode | null;

  private readonly shots: OneShotPool;
  private readonly output: GainNode;

  constructor(context: AudioContext, bank: AudioBank, destination: AudioNode, rng: Rng) {
    this.context = context;
    this.bank = bank;
    this.rng = rng;

    this.output = gainNode(context, 1);
    this.output.connect(destination);

    this.dry = gainNode(context, 1);
    this.dry.connect(this.output);
    this.shots = new OneShotPool(context, this.dry, rng, 16);

    const createDelay = (context as { createDelay?: (max: number) => DelayNode }).createDelay;
    if (typeof createDelay === 'function') {
      this.roomDelay = createDelay.call(context, 0.5);
      this.roomDelay.delayTime.value = 0.038;
      this.roomTone = lowpass(context, 1_800, 0.9);
      this.roomFeedback = gainNode(context, 0.32);
      this.roomLevel = gainNode(context, 0.4);

      this.dry.connect(this.roomDelay);
      this.roomDelay.connect(this.roomTone);
      this.roomTone.connect(this.roomFeedback);
      // The loop. Feedback below 0.4 with a lowpass in the path decays in about a third of a
      // second and cannot run away, which is the only thing that matters about a feedback loop.
      this.roomFeedback.connect(this.roomDelay);
      this.roomTone.connect(this.roomLevel);
      this.roomLevel.connect(this.output);
    } else {
      this.roomDelay = null;
      this.roomTone = null;
      this.roomFeedback = null;
      this.roomLevel = null;
    }
  }

  /**
   * One round leaving the barrel.
   *
   * The pitch jitter is not decoration. A cannon at 6000 rounds a minute fires 100 times a
   * second, and 100 bit-identical copies of one transient a second is not a gun — it is a
   * 100 Hz buzz, because that is literally what periodic repetition of a waveform is. A few per
   * cent of randomisation breaks the periodicity and it becomes a gun again.
   */
  cannon(gain = 0.85): void {
    this.shots.play(this.bank.cannon, gain, 1, 0.07);
  }

  /**
   * A missile leaving the rail: a rising whoosh that sweeps down as it goes away.
   *
   * The downward sweep is doing double duty — it is the rocket motor's own spectrum shifting as
   * the plume develops, and it is the Doppler of something accelerating away from you. Both
   * point the same way, which is why one sweep sells it.
   */
  missile(gain = 0.7): void {
    const context = this.context;
    const now = context.currentTime;
    const duration = 1.5;

    const source = context.createBufferSource();
    source.buffer = this.bank.white;
    source.loop = true;
    source.playbackRate.value = 1;

    const band = context.createBiquadFilter();
    band.type = 'bandpass';
    band.Q.value = 1.6;
    band.frequency.setValueAtTime(2_600, now);
    band.frequency.exponentialRampToValueAtTime(300, now + duration);

    const amp = context.createGain();
    amp.gain.value = 0;
    // A slower attack than a percussive one-shot: a rocket motor comes up over about 80 ms,
    // and an instant one sounds like a gunshot with a tail.
    percussive(amp.gain, gain, 0.08, duration, now);

    source.connect(band);
    band.connect(amp);
    amp.connect(this.dry);

    source.start(now);
    source.stop(now + duration + 0.1);
    source.onended = (): void => {
      disconnectAll([source, band, amp]);
      source.onended = null;
    };
  }

  /**
   * Something exploding, `distanceM` away.
   *
   * Distance does two things and they are separable: it takes level away (inverse distance) and
   * it takes *treble* away (air absorption). The second is what makes range legible — a close
   * explosion cracks, a distant one thuds — and it is why the lowpass is set from the range at
   * the moment of firing rather than being a fixed tone.
   */
  explosion(distanceM = 60, gain = 1): void {
    const context = this.context;
    const now = context.currentTime;

    const source = context.createBufferSource();
    source.buffer = this.bank.explosion;
    source.playbackRate.value = this.rng.range(0.85, 1.15);

    const tone = context.createBiquadFilter();
    tone.type = 'lowpass';
    tone.Q.value = 0.9;
    setImmediate(tone.frequency, explosionCutoffHz(distanceM), now);

    const amp = context.createGain();
    amp.gain.value = 0;
    percussive(amp.gain, gain * distanceGain(distanceM, 0.35), 0.006, 2, now);

    source.connect(tone);
    tone.connect(amp);
    amp.connect(this.dry);
    source.start(now);
    source.onended = (): void => {
      disconnectAll([source, tone, amp]);
      source.onended = null;
    };
  }

  dispose(): void {
    disconnectAll([
      this.dry,
      this.roomDelay,
      this.roomTone,
      this.roomFeedback,
      this.roomLevel,
      this.output,
    ]);
  }
}
